import React, { useState, useRef } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  VolumeUp as VolumeUpIcon,
  Translate as TranslateIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  ContentCopy as CopyIcon,
  Highlight as HighlightIcon,
  VolumeUp as VolumeIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { doc, setDoc, collection, addDoc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import SpaceTheme from '../components/SpaceTheme'

const BookDetail = ({ bookData, onBack }) => {
  const { user } = useAuth()
  const [selectedText, setSelectedText] = useState('')
  const [turkishTranslation, setTurkishTranslation] = useState('')
  const [manualText, setManualText] = useState('')
  const [translationDialogOpen, setTranslationDialogOpen] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [currentSpeed, setCurrentSpeed] = useState(1.0)
  const [speedDialogOpen, setSpeedDialogOpen] = useState(false)
  const [isTranslating, setIsTranslating] = useState(false)
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 })
  const [highlightRanges, setHighlightRanges] = useState([])
  const [currentHighlightColor, setCurrentHighlightColor] = useState('#ffff00') // Sarı
  const [currentColorName, setCurrentColorName] = useState('Sarı')
  const [showHighlightDialog, setShowHighlightDialog] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const audioRef = useRef(null)
  const speechRef = useRef(null)

  // Component mount olduğunda işaretlemeleri yükle
  React.useEffect(() => {
    if (user && bookData) {
      loadHighlights()
    }
  }, [user, bookData])

  const handleTextSelection = (event) => {
    const selection = window.getSelection()
    if (selection.toString().trim()) {
      const selectedText = selection.toString().trim()
      setSelectedText(selectedText)
      
      // Mouse pozisyonunu al
      setMenuPosition({
        x: event.clientX,
        y: event.clientY
      })
      setShowOptionsMenu(true)
    }
  }

  const autoTranslate = async (text) => {
    setIsTranslating(true)
    try {
      // Android uygulamasındaki aynı Google Translate API
      const API_KEY = 'AIzaSyBLJUFQ5Hu31zJ3Rhzkgy4CwN1MeQiZP_w'
      const GOOGLE_TRANSLATE_URL = 'https://translation.googleapis.com/language/translate/v2'
      
      const response = await fetch(`${GOOGLE_TRANSLATE_URL}?key=${API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'Englix-Education-App/1.0',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          q: text,
          source: 'en',
          target: 'tr',
          format: 'text'
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.data && data.data.translations && data.data.translations[0]) {
          const translatedText = data.data.translations[0].translatedText
          setTurkishTranslation(translatedText)
          toast.success('Çeviri tamamlandı!')
        } else {
          throw new Error('API çeviri sonucu geçersiz')
        }
      } else {
        const errorData = await response.json()
        throw new Error(`API hatası: ${response.status} - ${errorData.error?.message || 'Bilinmeyen hata'}`)
      }
    } catch (error) {
      console.error('Auto translation error:', error)
      setTurkishTranslation('')
      toast.error('Otomatik çeviri başarısız. Lütfen manuel olarak çevirin.')
    } finally {
      setIsTranslating(false)
    }
  }

  const speakSelectedText = () => {
    if (!selectedText) {
      toast.error('Seçili metin yok!')
      return
    }

    if ('speechSynthesis' in window) {
      // Eğer zaten konuşuyorsa durdur
      if (isSpeaking) {
        window.speechSynthesis.cancel()
        setIsSpeaking(false)
        setIsPaused(false)
      }
      
      // Seçili metni oku
      const utterance = new SpeechSynthesisUtterance(selectedText)
      utterance.lang = 'en-US'
      utterance.rate = currentSpeed
      utterance.onend = () => {
        setIsSpeaking(false)
        setIsPaused(false)
      }
      
      window.speechSynthesis.speak(utterance)
      setIsSpeaking(true)
      toast.success('Seçili metin okunuyor...')
    } else {
      toast.error('Sesli okuma başlatılamadı!')
    }
  }

  const handleSaveToMemory = async () => {
    if (!selectedText.trim() || !turkishTranslation.trim()) {
      toast.error('Çeviri yapılmadan hafızaya kaydedilemez')
      return
    }

    try {
      // Android uygulamasındaki aynı yapıyı kullan
      const documentId = `${selectedText}_en_tr`
      
      const memoryData = {
        originalText: selectedText,
        translatedText: turkishTranslation,
        fromLanguage: 'en',
        toLanguage: 'tr',
        timestamp: Date.now()
      }

      console.log('Saving to memory with Android structure:', memoryData)
      console.log('Document ID:', documentId)
      console.log('Firebase path:', `users/${user.uid}/memory/${documentId}`)
      
      await setDoc(
        doc(db, 'users', user.uid, 'memory', documentId),
        memoryData
      )
      
      console.log('✅ Memory saved successfully to Firebase')
      toast.success('Hafızaya kaydedildi!')
      setTranslationDialogOpen(false)
      setSelectedText('')
      setTurkishTranslation('')
      setManualText('')
    } catch (error) {
      console.error('❌ Error saving to memory:', error)
      toast.error('Hafızaya kaydedilirken hata oluştu')
    }
  }

  const handleManualTranslate = async () => {
    if (!manualText) {
      toast.error('Lütfen çevrilecek metni girin')
      return
    }

    setIsTranslating(true)
    try {
      // Android uygulamasındaki aynı Google Translate API
      const API_KEY = 'AIzaSyBLJUFQ5Hu31zJ3Rhzkgy4CwN1MeQiZP_w'
      const GOOGLE_TRANSLATE_URL = 'https://translation.googleapis.com/language/translate/v2'
      
      const response = await fetch(`${GOOGLE_TRANSLATE_URL}?key=${API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'Englix-Education-App/1.0',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          q: manualText,
          source: 'en',
          target: 'tr',
          format: 'text'
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.data && data.data.translations && data.data.translations[0]) {
          const translatedText = data.data.translations[0].translatedText
          setTurkishTranslation(translatedText)
          // Seçili metni güncelle
          setSelectedText(manualText)
          toast.success('Çeviri tamamlandı!')
        } else {
          throw new Error('API çeviri sonucu geçersiz')
        }
      } else {
        const errorData = await response.json()
        throw new Error(`API hatası: ${response.status} - ${errorData.error?.message || 'Bilinmeyen hata'}`)
      }
    } catch (error) {
      console.error('Translation error:', error)
      toast.error('Çeviri sırasında hata oluştu')
    } finally {
      setIsTranslating(false)
    }
  }

  const handleSpeak = () => {
    if ('speechSynthesis' in window) {
      if (isSpeaking) {
        window.speechSynthesis.cancel()
        setIsSpeaking(false)
        setIsPaused(false)
      } else {
        setSpeedDialogOpen(true)
      }
    } else {
      toast.error('Sesli okuma bu tarayıcıda desteklenmiyor')
    }
  }

  const startSpeaking = (speed) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(bookData.content)
      utterance.lang = 'en-US'
      utterance.rate = speed
      utterance.onend = () => {
        setIsSpeaking(false)
        setIsPaused(false)
      }
      utterance.onpause = () => setIsPaused(true)
      utterance.onresume = () => setIsPaused(false)
      
      speechRef.current = utterance
      window.speechSynthesis.speak(utterance)
      setIsSpeaking(true)
      setCurrentSpeed(speed)
      setSpeedDialogOpen(false)
    }
  }

  const handlePauseResume = () => {
    if (isPaused) {
      window.speechSynthesis.resume()
    } else {
      window.speechSynthesis.pause()
    }
  }

  const handleClearSelection = () => {
    setSelectedText('')
    setTurkishTranslation('')
    setManualText('')
    setTranslationDialogOpen(false)
    setShowOptionsMenu(false)
  }

  const handleTranslate = () => {
    setShowOptionsMenu(false)
    setManualText(selectedText) // Seçilen metni manuel input'a yaz
    autoTranslate(selectedText)
    setTranslationDialogOpen(true)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(selectedText)
    toast.success('Metin kopyalandı!')
    setShowOptionsMenu(false)
  }

  const handleHighlight = () => {
    setShowOptionsMenu(false)
    
    // Seçili metnin pozisyonunu bul
    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      const container = range.commonAncestorContainer
      
      // Metin içeriğindeki pozisyonu hesapla
      const textContent = bookData.content
      const selectedText = selection.toString().trim()
      
      if (selectedText) {
        const start = textContent.indexOf(selectedText)
        const end = start + selectedText.length
        
        if (start !== -1) {
          const newRange = {
            start,
            end,
            text: selectedText,
            color: currentHighlightColor,
            colorName: currentColorName
          }
          
          // Eğer bu aralık zaten işaretliyse, işaretini kaldır
          const existingIndex = highlightRanges.findIndex(
            range => range.start === start && range.end === end
          )
          
          let updatedRanges
          if (existingIndex !== -1) {
            updatedRanges = highlightRanges.filter((_, index) => index !== existingIndex)
            setHighlightRanges(updatedRanges)
            toast.success('İşaretleme kaldırıldı!')
          } else {
            // Yeni işaretleme ekle
            updatedRanges = [...highlightRanges, newRange]
            setHighlightRanges(updatedRanges)
            toast.success(`${currentColorName} renkte işaretlendi!`)
          }
          
          // Güncellenmiş veriyi Firestore'a kaydet
          saveHighlightsWithData(updatedRanges)
        }
      }
    }
  }

  const handleReadSelected = () => {
    setShowOptionsMenu(false)
    speakSelectedText()
  }

  // Hex color'ı Android integer formatına çevir
  const hexToAndroidColor = (hexColor) => {
    // # ile başlıyorsa kaldır
    const hex = hexColor.replace('#', '')
    const red = parseInt(hex.substr(0, 2), 16)
    const green = parseInt(hex.substr(2, 2), 16)
    const blue = parseInt(hex.substr(4, 2), 16)
    
    // Android ARGB formatı (alpha=255, red, green, blue)
    return (255 << 24) | (red << 16) | (green << 8) | blue
  }

  // İşaretleme fonksiyonları
  const saveHighlights = async () => {
    try {
      // Android uygulamasındaki aynı yapıyı kullan
      const bookKey = bookData.title.replace(/[^a-zA-Z0-9]/g, '_')
      
      const highlightsData = highlightRanges.map(range => ({
        start: range.start,
        end: range.end,
        text: range.text,
        color: hexToAndroidColor(range.color), // Hex'i Android integer'a çevir
        colorName: range.colorName
      }))

      const documentData = {
        bookTitle: bookData.title,
        highlights: highlightsData,
        lastUpdated: new Date()
      }
      
      console.log('Saving to Firebase path:', `users/${user.uid}/bookHighlights/${bookKey}`)
      console.log('Document data:', documentData)
      console.log('Original highlightRanges:', highlightRanges)
      console.log('Converted highlightsData:', highlightsData)
      
      await setDoc(
        doc(db, 'users', user.uid, 'bookHighlights', bookKey), 
        documentData
      )
      console.log('✅ Highlights saved successfully to Firebase')
    } catch (error) {
      console.error('❌ Error saving highlights:', error)
    }
  }

  // Güncellenmiş veriyi kaydetmek için yeni fonksiyon
  const saveHighlightsWithData = async (ranges) => {
    try {
      // Android uygulamasındaki aynı yapıyı kullan
      const bookKey = bookData.title.replace(/[^a-zA-Z0-9]/g, '_')
      
      const highlightsData = ranges.map(range => ({
        start: range.start,
        end: range.end,
        text: range.text,
        color: hexToAndroidColor(range.color), // Hex'i Android integer'a çevir
        colorName: range.colorName
      }))

      const documentData = {
        bookTitle: bookData.title,
        highlights: highlightsData,
        lastUpdated: new Date()
      }
      
      console.log('Saving updated highlights to Firebase path:', `users/${user.uid}/bookHighlights/${bookKey}`)
      console.log('Updated document data:', documentData)
      console.log('Updated ranges:', ranges)
      console.log('Converted highlightsData:', highlightsData)
      
      await setDoc(
        doc(db, 'users', user.uid, 'bookHighlights', bookKey), 
        documentData
      )
      console.log('✅ Updated highlights saved successfully to Firebase')
    } catch (error) {
      console.error('❌ Error saving updated highlights:', error)
    }
  }

  // Android color integer'ını hex'e çevir
  const androidColorToHex = (androidColor) => {
    // Android color değerleri ARGB formatında
    const alpha = (androidColor >> 24) & 0xFF
    const red = (androidColor >> 16) & 0xFF
    const green = (androidColor >> 8) & 0xFF
    const blue = androidColor & 0xFF
    
    return `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`
  }

  const loadHighlights = async () => {
    try {
      // Android uygulamasındaki aynı yapıyı kullan
      const bookKey = bookData.title.replace(/[^a-zA-Z0-9]/g, '_')
      
      console.log('Loading from Firebase path:', `users/${user.uid}/bookHighlights/${bookKey}`)
      console.log('Book title:', bookData.title)
      console.log('Book key:', bookKey)
      
      const docRef = doc(db, 'users', user.uid, 'bookHighlights', bookKey)
      const docSnap = await getDoc(docRef)
      
      console.log('Document exists:', docSnap.exists())
      
      if (docSnap.exists()) {
        const data = docSnap.data()
        console.log('Raw Firebase data:', data)
        
        if (data.highlights) {
          console.log('Raw highlights from Firebase:', data.highlights)
          
          // Android color formatını PWA formatına çevir
          const convertedHighlights = data.highlights.map(highlight => {
            const converted = {
              ...highlight,
              color: typeof highlight.color === 'number' ? androidColorToHex(highlight.color) : highlight.color
            }
            console.log('Converting highlight:', highlight, '→', converted)
            return converted
          })
          
          setHighlightRanges(convertedHighlights)
          console.log('✅ Final converted highlights:', convertedHighlights)
        } else {
          console.log('❌ No highlights found in document')
        }
      } else {
        console.log('❌ Document does not exist')
      }
    } catch (error) {
      console.error('❌ Error loading highlights:', error)
    }
  }

  const clearAllHighlights = () => {
    setHighlightRanges([])
    saveHighlightsWithData([])
    toast.success('Tüm işaretlemeler temizlendi!')
  }

  const deleteHighlight = (index) => {
    const newRanges = highlightRanges.filter((_, i) => i !== index)
    setHighlightRanges(newRanges)
    saveHighlightsWithData(newRanges)
    toast.success('İşaretleme kaldırıldı!')
  }

  const changeHighlightColor = (color, colorName) => {
    setCurrentHighlightColor(color)
    setCurrentColorName(colorName)
    setShowColorPicker(false)
    toast.success(`İşaretleme rengi: ${colorName}`)
  }

  // İşaretlemeli metni oluştur
  const getHighlightedText = () => {
    if (highlightRanges.length === 0) {
      return bookData.content
    }

    let result = bookData.content
    const sortedRanges = [...highlightRanges].sort((a, b) => b.start - a.start)
    
    sortedRanges.forEach(range => {
      const before = result.substring(0, range.start)
      const highlighted = result.substring(range.start, range.end)
      const after = result.substring(range.end)
      
      result = before + 
        `<span style="background-color: ${range.color}; padding: 1px 2px; border-radius: 2px;">${highlighted}</span>` + 
        after
    })
    
    return result
  }

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => onBack()}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {bookData.title}
          </Typography>
          
                     {/* İşaretleme yönetimi butonu */}
           <IconButton
             color="inherit"
             onClick={() => setShowHighlightDialog(true)}
             sx={{ mr: 1 }}
           >
             <HighlightIcon />
           </IconButton>
           
           {/* Sesli okuma butonu */}
           <IconButton
             color="inherit"
             onClick={handleSpeak}
             sx={{ mr: 1 }}
           >
             <VolumeIcon />
           </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Book Cover */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img
            src={bookData.imageUrl || '/sample-book.jpg'}
            alt={bookData.title}
            style={{
              width: 200,
              height: 300,
              objectFit: 'cover',
              borderRadius: 8,
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
            onError={(e) => {
              e.target.style.display = 'none'
            }}
          />
        </Box>

        {/* Translation Tools */}
        <Card sx={{ mb: 3, mx: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Çeviri Araçları
            </Typography>
                                                                         <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<TranslateIcon />}
                  onClick={() => setTranslationDialogOpen(true)}
                  size="small"
                >
                  Manuel Çeviri
                </Button>
              </Box>
                          <Typography variant="body2" color="text.secondary">
                ⚠️ Metni seçerek okuma, çeviri ve işaretleme işlemi yapabilirsiniz!
              </Typography>
          </CardContent>
        </Card>

        {/* Book Content */}
        <Card sx={{ mx: 2 }}>
          <CardContent>
                         <Box
               sx={{
                 lineHeight: 1.8,
                 fontSize: '16px',
                 userSelect: 'text',
                 cursor: 'text'
               }}
               onMouseUp={handleTextSelection}
               dangerouslySetInnerHTML={{ __html: getHighlightedText() }}
             />
          </CardContent>
        </Card>
      </Container>

             {/* Speed Selection Dialog */}
       <Dialog
         open={speedDialogOpen}
         onClose={() => setSpeedDialogOpen(false)}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>
           Okuma Hızını Seçin
         </DialogTitle>
         <DialogContent>
           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
             <Button
               variant="outlined"
               onClick={() => startSpeaking(0.25)}
               sx={{ justifyContent: 'flex-start', p: 2 }}
             >
               Çok Yavaş (0.25x)
             </Button>
             <Button
               variant="outlined"
               onClick={() => startSpeaking(0.5)}
               sx={{ justifyContent: 'flex-start', p: 2 }}
             >
               Yavaş (0.5x)
             </Button>
             <Button
               variant="outlined"
               onClick={() => startSpeaking(1.0)}
               sx={{ justifyContent: 'flex-start', p: 2 }}
             >
               Normal (1.0x)
             </Button>
             <Button
               variant="outlined"
               onClick={() => startSpeaking(1.5)}
               sx={{ justifyContent: 'flex-start', p: 2 }}
             >
               Hızlı (1.5x)
             </Button>
             <Button
               variant="outlined"
               onClick={() => startSpeaking(2.0)}
               sx={{ justifyContent: 'flex-start', p: 2 }}
             >
               Çok Hızlı (2.0x)
             </Button>
           </Box>
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setSpeedDialogOpen(false)}>
             İptal
           </Button>
         </DialogActions>
       </Dialog>

       {/* Translation Dialog */}
       <Dialog
         open={translationDialogOpen}
         onClose={() => setTranslationDialogOpen(false)}
         maxWidth="sm"
         fullWidth
       >
                 <DialogTitle>
           Çeviri
           <IconButton
             onClick={handleClearSelection}
             sx={{ position: 'absolute', right: 8, top: 8 }}
           >
             <ClearIcon />
           </IconButton>
         </DialogTitle>
                 <DialogContent>
           <TextField
             fullWidth
             label="İngilizce Metin"
             value={manualText}
             onChange={(e) => setManualText(e.target.value)}
             placeholder="Çevirmek istediğiniz metni buraya yazın..."
             sx={{ mb: 2 }}
           />
           
           <TextField
             fullWidth
             label="Türkçe Çeviri"
             multiline
             rows={3}
             value={turkishTranslation}
             onChange={(e) => setTurkishTranslation(e.target.value)}
             placeholder={isTranslating ? "Çeviriliyor..." : "Otomatik çeviri sonucu burada görünecek"}
             disabled={isTranslating}
             sx={{ mb: 2 }}
           />
           
           <Box sx={{ display: 'flex', gap: 1 }}>
             <Button
               variant="outlined"
               startIcon={<TranslateIcon />}
               onClick={handleManualTranslate}
               disabled={isTranslating}
               size="small"
             >
               {isTranslating ? "Çeviriliyor..." : "Çevir"}
             </Button>
           </Box>
         </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranslationDialogOpen(false)}>
            İptal
          </Button>
          <Button
            onClick={handleSaveToMemory}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Hafızaya Kaydet
          </Button>
                 </DialogActions>
       </Dialog>

       {/* Options Menu */}
       {showOptionsMenu && (
         <Box
           sx={{
             position: 'fixed',
             top: menuPosition.y - 60,
             left: menuPosition.x - 100,
             zIndex: 9999,
             backgroundColor: 'white',
             borderRadius: 2,
             boxShadow: 3,
             border: '1px solid #e0e0e0',
             p: 1
           }}
         >
           <Box sx={{ display: 'flex', gap: 0.5 }}>
             <Button
               size="small"
               startIcon={<TranslateIcon />}
               onClick={handleTranslate}
               sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
             >
               Çevir
             </Button>
             <Button
               size="small"
               startIcon={<VolumeUpIcon />}
               onClick={handleReadSelected}
               sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
             >
               Oku
             </Button>
             <Button
               size="small"
               startIcon={<CopyIcon />}
               onClick={handleCopy}
               sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
             >
               Kopyala
             </Button>
             <Button
               size="small"
               startIcon={<HighlightIcon />}
               onClick={handleHighlight}
               sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
             >
               İşaretle
             </Button>
           </Box>
         </Box>
       )}

               {/* Overlay to close menu when clicking outside */}
        {showOptionsMenu && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998
            }}
            onClick={() => setShowOptionsMenu(false)}
          />
        )}

        {/* İşaretleme Yönetimi Dialog */}
        <Dialog
          open={showHighlightDialog}
          onClose={() => setShowHighlightDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            İşaretleme Yönetimi
            <IconButton
              onClick={() => setShowHighlightDialog(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <ClearIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {highlightRanges.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <HighlightIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Henüz İşaretleme Yok
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Metni seçerek işaretleme yapabilirsiniz
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowColorPicker(true)}
                    startIcon={<HighlightIcon />}
                  >
                    Renk Seç ({currentColorName})
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={clearAllHighlights}
                  >
                    Tümünü Temizle
                  </Button>
                </Box>
                
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {highlightRanges.map((range, index) => (
                    <Card key={index} sx={{ mb: 1 }}>
                      <CardContent sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ 
                              backgroundColor: range.color, 
                              padding: '2px 4px', 
                              borderRadius: 1,
                              display: 'inline-block',
                              mb: 1
                            }}>
                              {range.text.length > 50 ? range.text.substring(0, 50) + '...' : range.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Pozisyon: {range.start}-{range.end} | Renk: {range.colorName}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => deleteHighlight(index)}
                            color="error"
                          >
                            <ClearIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Renk Seçici Dialog */}
        <Dialog
          open={showColorPicker}
          onClose={() => setShowColorPicker(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>İşaretleme Rengi Seçin</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 1 }}>
              {[
                { color: '#ffff00', name: 'Sarı' },
                { color: '#ffeb3b', name: 'Altın Sarı' },
                { color: '#ff9800', name: 'Turuncu' },
                { color: '#ff5722', name: 'Kırmızı' },
                { color: '#e91e63', name: 'Pembe' },
                { color: '#9c27b0', name: 'Mor' },
                { color: '#2196f3', name: 'Mavi' },
                { color: '#00bcd4', name: 'Cyan' },
                { color: '#4caf50', name: 'Yeşil' }
              ].map((colorOption) => (
                <Button
                  key={colorOption.color}
                  variant="outlined"
                  onClick={() => changeHighlightColor(colorOption.color, colorOption.name)}
                  sx={{
                    backgroundColor: colorOption.color,
                    color: '#000',
                    border: currentHighlightColor === colorOption.color ? '3px solid #1976d2' : '1px solid #ccc',
                    '&:hover': {
                      backgroundColor: colorOption.color,
                      opacity: 0.8
                    }
                  }}
                >
                  {colorOption.name}
                </Button>
              ))}
            </Box>
          </DialogContent>
        </Dialog>
    </SpaceTheme>
  )
}

export default BookDetail
